/**
 * Teachers onboarding — magic-link sign-in.
 *
 * Three-screen mobile-first flow per the product brief:
 *   1. class     — "Turn your class into a film" + class-name input
 *   2. email     — "Where should we send your secure link?"
 *   3. sent      — "Check your email"
 *
 * No password, no registration form, no "sign up" language. The class
 * name is persisted to localStorage alongside the magic-link request so
 * that on token consume (/teachers/auth) the backend can auto-create
 * their first classroom and drop them straight inside it.
 *
 * Design system is deliberately different from the rest of the app —
 * deep navy (#0B0F1A) + cyan/purple accent + Playfair serif headline.
 * Teachers respond to "made for me", not "another corporate tool".
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CaretRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = 'eventdrop_teacher_draft_class_name';

export default function TeachersStart() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('class'); // class | email | sent
  const [className, setClassName] = useState(
    () => (typeof window !== 'undefined' ? window.localStorage.getItem(DRAFT_KEY) || '' : ''),
  );
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const classInputRef = useRef(null);
  const emailInputRef = useRef(null);

  // Autofocus the active field on each phase for keyboard-first users
  useEffect(() => {
    const t = setTimeout(() => {
      if (phase === 'class') classInputRef.current?.focus();
      if (phase === 'email') emailInputRef.current?.focus();
    }, 200);
    return () => clearTimeout(t);
  }, [phase]);

  const onClassSubmit = (e) => {
    e?.preventDefault();
    const trimmed = className.trim();
    if (!trimmed) {
      setError('What should we call your class?');
      return;
    }
    if (trimmed.length > 120) {
      setError('Shorten that a little — 120 characters max.');
      return;
    }
    try { window.localStorage.setItem(DRAFT_KEY, trimmed); } catch { /* ignore */ }
    setError(null);
    setPhase('email');
  };

  const onEmailSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/teacher/magic-link`, {
        email: trimmed,
        draft_class_name: className.trim(),
      });
      setPhase('sent');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/teacher/magic-link`, {
        email: email.trim().toLowerCase(),
        draft_class_name: className.trim(),
      });
    } catch {
      setError('Could not resend — try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="teachers-start"
      style={{
        minHeight: '100vh',
        background: '#0B0F1A',
        color: '#FFFFFF',
        fontFamily: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        padding: '28px 22px 60px',
      }}
    >
      {/* Top bar — logo left, back button appears on email/sent phases */}
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 44 }}>
        <button
          onClick={() => navigate('/')}
          data-testid="teachers-start-logo"
          style={{
            background: 'none', border: 'none', color: '#FFFFFF',
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 18, fontWeight: 400, letterSpacing: '-0.01em',
            cursor: 'pointer', padding: 0,
          }}
        >
          EventDrop
        </button>
        {phase !== 'class' && (
          <button
            onClick={() => { setPhase(phase === 'sent' ? 'email' : 'class'); setError(null); }}
            data-testid="teachers-start-back"
            style={{
              marginLeft: 'auto',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.55)', fontSize: 13,
              cursor: 'pointer', padding: 0, letterSpacing: '0.02em',
            }}
          >
            <ArrowLeft size={12} weight="bold" /> Back
          </button>
        )}
      </header>

      <main style={{ maxWidth: 440, width: '100%', margin: '0 auto', flex: 1 }}>
        {phase === 'class' && (
          <ScreenClass
            className={className}
            setClassName={setClassName}
            onSubmit={onClassSubmit}
            error={error}
            inputRef={classInputRef}
            onLoginInstead={() => setPhase('email')}
          />
        )}
        {phase === 'email' && (
          <ScreenEmail
            email={email}
            setEmail={setEmail}
            className={className}
            onSubmit={onEmailSubmit}
            submitting={submitting}
            error={error}
            inputRef={emailInputRef}
          />
        )}
        {phase === 'sent' && (
          <ScreenSent
            email={email}
            onResend={resend}
            submitting={submitting}
            error={error}
          />
        )}
      </main>
    </div>
  );
}

/* ───────── Screen 1 ───────── */
const ScreenClass = ({ className, setClassName, onSubmit, error, inputRef, onLoginInstead }) => (
  <form onSubmit={onSubmit} data-testid="screen-class">
    <div
      style={{
        fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: '#D4AF37', marginBottom: 18,
      }}
      data-testid="teachers-start-eyebrow"
    >
      ✨ For teachers
    </div>
    <h1
      style={{
        fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
        fontSize: 40, fontWeight: 400, letterSpacing: '-0.015em',
        lineHeight: 1.1, margin: 0, marginBottom: 28,
      }}
    >
      Turn your class into a film
    </h1>
    <label
      htmlFor="class-name-input"
      style={{
        display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.55)',
        marginBottom: 10, letterSpacing: '0.02em',
      }}
    >
      What's your class called?
    </label>
    <InputPill
      id="class-name-input"
      ref={inputRef}
      placeholder="e.g. Year 6 — Mrs Smith"
      value={className}
      onChange={(e) => setClassName(e.target.value)}
      autoComplete="off"
      data-testid="teachers-start-class-input"
      maxLength={120}
    />
    {error && (
      <div
        data-testid="teachers-start-class-error"
        style={{ marginTop: 12, color: '#FF9F80', fontSize: 13 }}
      >{error}</div>
    )}
    <div style={{ marginTop: 24 }}>
      <PrimaryButton data-testid="teachers-start-class-submit" type="submit">
        Continue <CaretRight size={14} weight="bold" />
      </PrimaryButton>
    </div>
    <p
      style={{
        marginTop: 18, fontSize: 12, color: 'rgba(255,255,255,0.4)',
        lineHeight: 1.6, maxWidth: 340,
      }}
    >
      Takes 30 seconds · Private &amp; secure · No passwords
    </p>
    <div
      style={{
        marginTop: 44, paddingTop: 24,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        fontSize: 13, color: 'rgba(255,255,255,0.55)',
      }}
    >
      Already have a class?{' '}
      <button
        type="button"
        onClick={onLoginInstead}
        data-testid="teachers-start-login-instead"
        style={{
          background: 'none', border: 'none', padding: 0,
          color: '#5FD3F3', fontSize: 13, cursor: 'pointer',
          textDecoration: 'underline', textUnderlineOffset: 3,
        }}
      >
        Log in →
      </button>
    </div>
  </form>
);

/* ───────── Screen 2 ───────── */
const ScreenEmail = ({ email, setEmail, className, onSubmit, submitting, error, inputRef }) => (
  <form onSubmit={onSubmit} data-testid="screen-email" noValidate>
    <div
      style={{
        fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: '#D4AF37', marginBottom: 18,
      }}
    >
      ✨ Almost ready
    </div>
    <h1
      style={{
        fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
        fontSize: 32, fontWeight: 400, letterSpacing: '-0.01em',
        lineHeight: 1.2, margin: 0, marginBottom: 14,
      }}
    >
      Where should we send your secure link?
    </h1>
    {className && (
      <p
        data-testid="teachers-start-class-echo"
        style={{
          fontSize: 13, color: 'rgba(255,255,255,0.55)',
          marginTop: 0, marginBottom: 24,
        }}
      >
        Setting up <strong style={{ color: '#FFFFFF', fontWeight: 500 }}>{className}</strong>
      </p>
    )}
    <InputPill
      ref={inputRef}
      type="email"
      placeholder="you@school.org"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      autoComplete="email"
      data-testid="teachers-start-email-input"
    />
    {error && (
      <div
        data-testid="teachers-start-email-error"
        style={{ marginTop: 12, color: '#FF9F80', fontSize: 13 }}
      >{error}</div>
    )}
    <div style={{ marginTop: 24 }}>
      <PrimaryButton
        data-testid="teachers-start-email-submit"
        type="submit"
        disabled={submitting}
      >
        {submitting ? 'Sending…' : <>Send secure link <CaretRight size={14} weight="bold" /></>}
      </PrimaryButton>
    </div>
    <p
      style={{
        marginTop: 16, fontSize: 12, color: 'rgba(255,255,255,0.45)',
        lineHeight: 1.6,
      }}
    >
      No password, just one tap from your email
    </p>
    <div
      style={{
        marginTop: 36,
        padding: '18px 20px',
        background: 'rgba(95,211,243,0.05)',
        border: '1px solid rgba(95,211,243,0.14)',
        borderRadius: 14,
        fontSize: 13,
        color: 'rgba(255,255,255,0.72)',
        lineHeight: 1.7,
      }}
      data-testid="teachers-start-trust"
    >
      <div style={{ marginBottom: 4 }}>🔒 Only you can manage your class</div>
      <div>👨‍👩‍👧 Parents get a read-only link</div>
    </div>
  </form>
);

/* ───────── Screen 3 ───────── */
const ScreenSent = ({ email, onResend, submitting, error }) => (
  <div data-testid="screen-sent">
    <div
      style={{
        fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: '#D4AF37', marginBottom: 18,
      }}
    >
      ✨ Check your email
    </div>
    <h1
      style={{
        fontFamily: 'Playfair Display, Georgia, "Times New Roman", serif',
        fontSize: 32, fontWeight: 400, letterSpacing: '-0.01em',
        lineHeight: 1.2, margin: 0, marginBottom: 14,
      }}
    >
      We've sent a secure link
    </h1>
    <p
      data-testid="teachers-start-sent-email"
      style={{
        fontSize: 15, color: 'rgba(255,255,255,0.85)',
        fontFamily: "Georgia, 'Times New Roman', serif",
        marginTop: 0, marginBottom: 28,
      }}
    >
      {email}
    </p>
    <p
      style={{
        fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7,
        marginBottom: 24,
      }}
    >
      Open the email on this device and tap the link — we'll take you
      straight inside your classroom.
    </p>
    <button
      onClick={onResend}
      disabled={submitting}
      data-testid="teachers-start-resend"
      style={{
        background: 'none', border: 'none', padding: 0,
        color: '#5FD3F3', fontSize: 13, cursor: 'pointer',
        textDecoration: 'underline', textUnderlineOffset: 3,
        opacity: submitting ? 0.5 : 1,
      }}
    >
      {submitting ? 'Resending…' : 'Didn\'t get it? Resend link'}
    </button>
    {error && (
      <div
        data-testid="teachers-start-resend-error"
        style={{ marginTop: 12, color: '#FF9F80', fontSize: 13 }}
      >{error}</div>
    )}
    <div
      style={{
        marginTop: 48, paddingTop: 24,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        fontSize: 12, color: 'rgba(255,255,255,0.35)',
        lineHeight: 1.6,
      }}
    >
      The link expires in 15 minutes · single use · safe to ignore if you
      didn't request it.
    </div>
  </div>
);

/* ───────── shared pieces ───────── */
const InputPill = React.forwardRef(function InputPill(props, ref) {
  return (
    <input
      ref={ref}
      {...props}
      style={{
        width: '100%',
        height: 56,
        padding: '0 20px',
        borderRadius: 14,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: '#FFFFFF',
        fontSize: 15,
        fontFamily: 'inherit',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#5FD3F3';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(95,211,243,0.12)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
        props.onBlur?.(e);
      }}
    />
  );
});

const PrimaryButton = ({ children, disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      gap: 6,
      height: 52,
      padding: '0 28px',
      minWidth: 200,
      borderRadius: 999,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled
        ? 'rgba(255,255,255,0.18)'
        : 'linear-gradient(90deg, #B89762 0%, #4FC3F7 100%)',
      color: '#FFFFFF',
      fontWeight: 600,
      fontSize: 15,
      letterSpacing: '0.02em',
      transition: 'transform 120ms ease, filter 120ms ease',
    }}
    onMouseEnter={(e) => {
      if (!disabled) e.currentTarget.style.filter = 'brightness(1.08)';
    }}
    onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
    onMouseDown={(e) => {
      if (!disabled) e.currentTarget.style.transform = 'scale(0.98)';
    }}
    onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
  >
    {children}
  </button>
);
